import type { CapturedNetworkRequest, CaptureResult, Properties } from 'posthog-js'
import { PATIENT_PATHS } from '@/utilities/paths'

const FIX_DATA_SECRET_PREFIX = `${PATIENT_PATHS.fixData}/`
const REDACTED_SECRET_SEGMENT = ':secret'

/** Path prefixes whose next segment is a secret. The secret segment becomes `:secret`. */
const SECRET_PATH_PREFIXES = [
  FIX_DATA_SECRET_PREFIX,
  '/v2/kit/correction/',
  // Browsers reach Portal API through the Next.js proxy outside local dev (see `getPortalApiBaseUrl`).
  '/api/pp-api/v2/kit/correction/',
] as const

// A secret path inside free text, such as an error message or an exception value.
const EMBEDDED_SECRET_PATH =
  /(\/fix-data(?:\/|%2F)|\/v2\/kit\/correction(?:\/|%2F))[^\s/"'?#&<>()[\]{},;]+/gi

const MAX_NESTED_PROPERTY_DEPTH = 4

/**
 * Replaces the secret segment with `:secret`. A fix-data link has nothing after its secret, so the whole
 * remainder is dropped, even when the first segment is blank. Other prefixes keep their sub-path,
 * such as `/suggestions`.
 */
function decodedPathname(pathname: string): string {
  try {
    return decodeURIComponent(pathname)
  } catch {
    return pathname
  }
}

function redactSecretPathSegment(url: URL): boolean {
  const pathname = decodedPathname(url.pathname)
  const prefix = SECRET_PATH_PREFIXES.find((candidate) => pathname.startsWith(candidate))
  if (!prefix) return false
  const remainder = pathname.slice(prefix.length)
  if (!remainder) return false
  if (prefix === FIX_DATA_SECRET_PREFIX) {
    url.pathname = `${prefix}${REDACTED_SECRET_SEGMENT}`
    return true
  }
  const segment = remainder.split('/')[0]
  if (!segment) return false
  url.pathname = `${prefix}${REDACTED_SECRET_SEGMENT}${remainder.slice(segment.length)}`
  return true
}

function isAshPortalHost(host: string): boolean {
  return host === 'localhost:3000' || host.endsWith('poweredbyash.io')
}

function redactBareSecretPathUrl(value: string): string {
  try {
    const url = new URL(value, 'https://redact.local')
    if (!redactSecretPathSegment(url)) return value
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return value
  }
}

/** Replaces secret paths inside free text, so messages and exception values cannot carry them. */
function redactEmbeddedSecretPaths(value: string): string {
  return value.replace(EMBEDDED_SECRET_PATH, `$1${REDACTED_SECRET_SEGMENT}`)
}

/**
 * Removes the Stytch `token` query param from Ash portal URLs and replaces the
 * `/fix-data/{secret}` and `/v2/kit/correction/{secret}` path segments with `:secret`.
 * Accepts full URLs or bare pathnames. Leaves other hosts' query params and
 * unparseable strings unchanged.
 */
export function redactSensitiveUrlParams(urlString: string): string {
  let url: URL
  try {
    url = new URL(urlString)
  } catch {
    return redactBareSecretPathUrl(urlString)
  }

  const hasStytchToken = isAshPortalHost(url.host) && url.searchParams.has('token')
  const strippedSecret = redactSecretPathSegment(url)
  if (!hasStytchToken && !strippedSecret) {
    return urlString
  }

  if (hasStytchToken) {
    url.searchParams.delete('token')
  }
  return url.toString()
}

/**
 * Redacts every string in the container, including nested arrays and objects such as `$exception_list`.
 * PostHog stores URLs under many keys, and new keys can appear, so no key list is used.
 * `$snapshot*` keys hold session replay data that PostHog sends as separate events, so they are skipped.
 */
function redactStringValues(container: Record<string, unknown>, depth: number): void {
  for (const key of Object.keys(container)) {
    if (key.startsWith('$snapshot')) continue
    const value = container[key]
    if (typeof value === 'string') {
      container[key] = redactEmbeddedSecretPaths(redactSensitiveUrlParams(value))
    } else if (value && typeof value === 'object' && depth < MAX_NESTED_PROPERTY_DEPTH) {
      redactStringValues(value as Record<string, unknown>, depth + 1)
    }
  }
}

function redactUrlProperties(properties: Properties): void {
  redactStringValues(properties, 0)
}

/** PostHog `before_send` hook: strip Stytch tokens and the fix-data secret from top-level string properties. */
export function sanitizePosthogEventUrls(event: CaptureResult | null): CaptureResult | null {
  if (!event) {
    return null
  }

  if (event.properties) {
    redactUrlProperties(event.properties)
  }
  if (event.$set) {
    redactUrlProperties(event.$set)
  }
  if (event.$set_once) {
    redactUrlProperties(event.$set_once)
  }

  return event
}

/**
 * PostHog `session_recording.maskCapturedNetworkRequestFn`. Session replay and performance capture
 * store request URLs outside the default URL properties, so they are redacted here. A request whose
 * URL changed carried a secret or a Stytch token, so its bodies are dropped as well.
 */
export function redactCapturedNetworkRequest(
  request: CapturedNetworkRequest,
): CapturedNetworkRequest {
  const name = redactSensitiveUrlParams(request.name)
  if (name === request.name) return request
  return { ...request, name, requestBody: null, responseBody: null }
}

/**
 * True when a stored string still has a raw segment after `fix-data/` or `fix-data%2F`.
 * The `:secret` placeholder this module writes is not a raw secret.
 */
function hasRawFixDataSecret(value: string): boolean {
  const pathName = PATIENT_PATHS.fixData.slice(1)
  const pattern = new RegExp(String.raw`${pathName}(?:/|%2F)([^/?#&\s"'\\;]*)`, 'gi')
  for (const match of value.matchAll(pattern)) {
    const rawSegment = match[1] ?? ''
    let segment = rawSegment
    try {
      segment = decodeURIComponent(rawSegment)
    } catch {
      segment = rawSegment
    }
    if (segment.length > 0 && segment !== REDACTED_SECRET_SEGMENT) return true
  }
  return false
}

/**
 * True when PostHog storage (`ph_*` in localStorage or cookies) still holds a fix-data URL with a
 * raw secret. A stored `:secret` placeholder is ignored. Builds that started PostHog before the
 * secret was stripped saved a raw one. Call before `posthog.init`.
 */
export function hasPersistedFixDataUrl(): boolean {
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index)
      const value = key?.startsWith('ph_') ? window.localStorage.getItem(key) : null
      if (value && hasRawFixDataSecret(value)) return true
    }
    return document.cookie
      .split(';')
      .some((cookie) => cookie.trim().startsWith('ph_') && hasRawFixDataSecret(cookie))
  } catch {
    return false
  }
}
